import { useState, useCallback } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { ReservationData, ReservationResult } from '../types/reservation.types';


export function useReservationSubmit(restaurantId: string) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(async (data: ReservationData): Promise<ReservationResult | null> => {
    if (!data.date || !data.timeSlot || !data.partySize) return null;
    setSubmitting(true);
    setError(null);

    try {
      // Fetch restaurant info + settings
      const [{ data: restaurant }, { data: settings }] = await Promise.all([
        supabase.from('restaurants').select('name, address, phone, email').eq('id', restaurantId).single(),
        supabase.from('reservation_settings').select('require_email_confirmation, reservation_email').eq('restaurant_id', restaurantId).maybeSingle(),
      ]);

      const requireConfirmation = settings?.require_email_confirmation ?? true;
      const formattedDate = format(data.date, 'yyyy-MM-dd');

      // Insert reservation
      const { data: reservation, error: dbError } = await supabase
        .from('reservations')
        .insert({
          restaurant_id: restaurantId,
          customer_name: data.guestName.trim(),
          customer_email: data.guestEmail.trim().toLowerCase(),
          customer_phone: data.guestPhone.trim() || null,
          reservation_date: formattedDate,
          reservation_time: data.timeSlot,
          party_size: data.partySize,
          special_requests: data.notes.trim() || null,
          zone_preference: data.zonePreference || null,
          table_id: data.tableId || null,
          status: requireConfirmation ? 'pending' : 'confirmed',
          confirmed_at: requireConfirmation ? null : new Date().toISOString(),
        })
        .select()
        .single();

      if (dbError) throw dbError;

      // Send email
      try {
        const notificationEmail = settings?.reservation_email || restaurant?.email || null;
        await supabase.functions.invoke('send-reservation-email', {
          body: {
            restaurantName: restaurant?.name || '',
            customerName: data.guestName.trim(),
            customerEmail: data.guestEmail.trim().toLowerCase(),
            customerPhone: data.guestPhone.trim() || null,
            date: format(data.date, 'dd MMMM yyyy', { locale: fr }),
            time: data.timeSlot,
            partySize: data.partySize,
            specialRequests: data.notes.trim() || null,
            restaurantEmail: notificationEmail,
            confirmationToken: requireConfirmation ? (reservation as any).confirmation_token : null,
            skipConfirmation: !requireConfirmation,
          },
        });
      } catch (emailErr) {
        console.error('Email send failed:', emailErr);
      }

      return {
        id: reservation.id,
        referenceCode: (reservation as any).reference_code || '',
        date: format(data.date, 'EEEE d MMMM yyyy', { locale: fr }),
        rawDate: formattedDate,
        time: data.timeSlot,
        partySize: data.partySize,
        customerEmail: data.guestEmail.trim().toLowerCase(),
        restaurantName: restaurant?.name || '',
        restaurantAddress: restaurant?.address || '',
        requiresEmailConfirmation: requireConfirmation,
      };
    } catch (err: any) {
      console.error('Reservation submit error:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
      return null;
    } finally {
      setSubmitting(false);
    }
  }, [restaurantId]);

  return { submit, submitting, error };
}
